import { motion } from "framer-motion";
import { CountUp, growX, growY, Reveal } from "./lib";
import { IMG, people } from "./content";
import { Divider } from "./Chrome";
import { ChartCard, HighlightsPanel, SimpleTable } from "./parts";
import { VIDEOS, VideoFeature } from "./Video";

function GivingChart() {
  const max = 18;
  return (
    <ChartCard title="Community investments" subtitle="Applied Materials, Inc. and the Applied Materials Foundation, $ millions" page={72} testId="am-chart-giving"
      table={<SimpleTable caption="Community investments by year" head={["Year", "Applied ($M)", "Foundation ($M)", "Total ($M)"]} rows={people.giving.map((g) => [g.year, g.applied, g.foundation, g.total])} />}>
      <div className="flex h-60 items-end gap-3 sm:gap-5" aria-hidden>
        {people.giving.map((g, i) => (
          <div key={g.year} className="flex h-full flex-1 flex-col items-center justify-end">
            <span className="mb-1 text-sm font-semibold am-accent">${g.total}M</span>
            <motion.div className="flex w-full max-w-[56px] origin-bottom flex-col" style={{ height: `${Math.round(((g.applied + g.foundation) / max) * 190)}px` }} {...growY(i * 0.08)}>
              <div className="w-full bg-[var(--am-purple)]" style={{ flex: g.foundation }} />
              <div className="w-full bg-[#a998de]" style={{ flex: g.applied }} />
            </motion.div>
            <span className="mt-2 text-xs">{g.year}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-4 text-xs">
        <span className="flex items-center gap-2"><i className="h-3 w-3 bg-[var(--am-purple)]" />Applied Materials Foundation</span>
        <span className="flex items-center gap-2"><i className="h-3 w-3 bg-[#a998de]" />Applied Materials, Inc.</span>
      </div>
    </ChartCard>
  );
}

function WomenChart() {
  return (
    <ChartCard title="Female representation of the global workforce" subtitle="FY 2025, % of each group" page={69} testId="am-chart-women"
      table={<SimpleTable caption="Female representation FY 2025" head={["Group", "%"]} rows={people.women.map((w) => [w.label, `${w.value}%`])} />}>
      <ul className="space-y-4">
        {people.women.map((w, i) => (
          <li key={w.label}>
            <div className="flex justify-between gap-3 text-sm"><span>{w.label}</span><CountUp value={`${w.value}%`} className="font-semibold am-accent" /></div>
            <div className="mt-1.5 h-2.5 w-full bg-[var(--am-mist)]">
              <motion.div className="h-full origin-left bg-[var(--am-purple)]" style={{ width: `${(w.value / 30) * 100}%` }} {...growX(i * 0.07)} />
            </div>
          </li>
        ))}
      </ul>
    </ChartCard>
  );
}

function WorkforceSafety() {
  const first = people.workforce[0].total;
  const last = people.workforce[people.workforce.length - 1].total;
  return (
    <ChartCard title="Workforce and safety" subtitle="Total workforce and FY 2025 incident rates" page={68} testId="am-chart-workforce"
      table={<SimpleTable caption="Total workforce by year" head={["Year", "Total workforce"]} rows={people.workforce.map((w) => [w.year, w.total.toLocaleString("en-US")])} />}>
      <CountUp value={last.toLocaleString("en-US")} className="block text-5xl font-semibold am-accent" />
      <p className="mt-1 text-sm">total workforce in FY 2025, up from {first.toLocaleString("en-US")} in FY 2019</p>
      <div className="mt-6 grid grid-cols-2 gap-3">
        {people.safety.map((s) => (
          <div key={s.label} className="border-l-2 border-[var(--am-purple)] pl-3">
            <CountUp value={s.values[0]} className="block text-2xl font-semibold text-[#2b2d30]" />
            <span className="text-xs leading-tight">{s.label}</span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs">FY 2024: TRIR 0.41, LTIR 0.20, DART 0.31.</p>
    </ChartCard>
  );
}

export function PeopleSection() {
  return (
    <section id="people" className="am-people scroll-mt-28" data-testid="am-section-people">
      <Divider id="people" number="03" title="Empowering People" items={["2025 Highlights", "Connected Coworkers", "Human Rights", "Community Impact"]}
        image="p26_0.jpg" alt="An employee recording a video on a phone mounted on a tripod" />
      <div className="mx-auto max-w-[1320px] px-5 py-20 sm:px-8 sm:py-28">
        <div className="grid gap-12 lg:grid-cols-[1fr_400px]">
          <div>
            <Reveal><p className="am-headline text-2xl sm:text-3xl lg:text-4xl" data-testid="am-people-headline">{people.headline}</p></Reveal>
            <div className="mt-10 grid gap-6 text-[15px] leading-relaxed sm:grid-cols-2">
              {people.intro.map((p) => <Reveal key={p.slice(0, 20)}><p>{p}</p></Reveal>)}
              <Reveal className="am-photo am-clip aspect-[3/2]"><img src={IMG("p28_0.jpg")} alt="Applied Materials employees" className="h-full w-full object-cover" loading="lazy" /></Reveal>
            </div>
          </div>
          <HighlightsPanel items={people.highlights} page={27} prefix="am-people" />
        </div>
        <VideoFeature video={VIDEOS.people} testId="am-video-people" />
        <h3 className="mt-20 text-2xl font-semibold am-accent sm:text-3xl">Our people in numbers</h3>
        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <GivingChart />
          <WomenChart />
          <WorkforceSafety />
        </div>
      </div>
    </section>
  );
}
